// src/api/worldGateway.js
import Gateway from './gateway';

class WorldGateway {
  // ==================== МИРЫ ====================
  
  /**
   * Получить список доступных миров
   * @returns {Promise<Array>} Массив миров
   */
  async getAvailableWorlds() {
    try {
      const response = await Gateway.http.world.get('/api/worlds');
      
      // Бэкенд возвращает { worlds: [...], pagination: {...} }
      const data = response.data;
      
      if (data.worlds && Array.isArray(data.worlds)) {
        return data.worlds;
      }
      
      console.error('Unexpected response format:', data);
      throw new Error('Invalid response format from /api/worlds');
    } catch (error) {
      console.error('❌ Failed to fetch available worlds:', error);
      throw error; // Без фалбеков и моковых данных!
    }
  }

  /**
   * Получить детальную информацию о мире
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Детали мира
   */
  async getWorldDetails(worldId) {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}`);
      // Бэкенд возвращает { world: {...} }
      return response.data.world;
    } catch (error) {
      console.error(`❌ Failed to fetch world details for ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Создать новый мир
   * @param {Object} worldConfig - Конфигурация мира
   * @returns {Promise<Object>} Результат создания
   */
  async createWorld(worldConfig) {
    try {
      const response = await Gateway.http.world.post('/api/worlds', worldConfig);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create world:', error);
      throw error;
    }
  }

  /**
   * Удалить мир
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteWorld(worldId) {
    try {
      const response = await Gateway.http.world.delete(`/api/worlds/${worldId}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Запустить симуляцию мира
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Результат запуска
   */
  async startWorldSimulation(worldId) {
    try {
      const response = await Gateway.http.world.post(`/api/worlds/${worldId}/start`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to start simulation for world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Приостановить симуляцию мира
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Результат паузы
   */
  async pauseWorldSimulation(worldId) {
    try {
      const response = await Gateway.http.world.post(`/api/worlds/${worldId}/pause`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to pause simulation for world ${worldId}:`, error);
      throw error;
    }
  }

  // ==================== ОБЪЕКТЫ МИРА ====================
  
  /**
   * Получить объекты мира по типу
   * ЕДИНЫЙ эндпоинт для любых типов объектов
   * 
   * @param {string} worldId - ID мира
   * @param {string} type - Тип объектов (например: 'planets', 'star_systems', 'galaxies', 'civilizations' и т.д.)
   * @returns {Promise<Array>} Массив объектов указанного типа
   * 
   * @example
   * // Загрузить планеты
   * const planets = await WorldGateway.getWorldObjects('world_123', 'planets');
   * 
   * // Загрузить звёздные системы
   * const systems = await WorldGateway.getWorldObjects('world_123', 'star_systems');
   * 
   * // Загрузить корабли
   * const ships = await WorldGateway.getWorldObjects('world_123', 'ships');
   */
  async getWorldObjects(worldId, type) {
    // Безопасная валидация: только латинские буквы и подчёркивания
    if (!/^[a-z_]+$/.test(type)) {
      throw new Error(`Invalid object type: ${type}. Allowed format: a-z_`);
    }
    
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/objects`, {
        params: { type }
      });
      
      // Формат ответа: { objects: [...], count: N }
      return response.data.objects || [];
    } catch (error) {
      console.warn(`⚠️ Failed to load ${type} for world ${worldId}:`, error.message);
      throw error; // Без фалбеков!
    }
  }

  // ==================== ТОЧКИ СПАУНА ====================
  
  /**
   * Получить точки спауна мира
   * @param {string} worldId - ID мира
   * @returns {Promise<Array>} Массив точек спауна
   */
  async getSpawnPoints(worldId) {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/spawn-points`);
      
      return response.data?.spawn_points || [];
    } catch (error) {
      console.warn(`⚠️ Failed to fetch spawn points for world ${worldId}:`, error);
      throw error;
    }
  }

  // ==================== СТАТИСТИКА И СТАТУС ====================
  
  /**
   * Получить статистику мира
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Статистика мира
   */
  async getWorldStatistics(worldId) {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/stats`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch statistics for world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Получить статус создания мира
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Статус создания
   */
  async getCreationStatus(worldId) {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/creation-status`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch creation status for world ${worldId}:`, error);
      throw error;
    }
  }

  // ==================== ЭКСПОРТ/ИМПОРТ ====================
  
  /**
   * Экспортировать мир
   * @param {string} worldId - ID мира
   * @param {string} format - Формат экспорта (например: 'json')
   * @returns {Promise<Object>} Экспортированные данные
   */
  async exportWorld(worldId, format = 'json') {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/export`, {
        params: { format }
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to export world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Импортировать мир
   * @param {Object} worldData - Данные для импорта
   * @returns {Promise<Object>} Результат импорта
   */
  async importWorld(worldData) {
    try {
      const response = await Gateway.http.world.post('/api/worlds/import', {
        world_data: worldData
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to import world:', error);
      throw error;
    }
  }

  // ==================== УПРАВЛЕНИЕ СИМУЛЯЦИЕЙ ====================
  
  /**
   * Получить статус симуляции
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Статус симуляции
   */
  async getSimulationStatus(worldId) {
    try {
      const response = await Gateway.http.world.get(`/api/worlds/${worldId}/simulation-status`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch simulation status for world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Обновить настройки мира
   * @param {string} worldId - ID мира
   * @param {Object} settings - Новые настройки
   * @returns {Promise<Object>} Обновлённые настройки
   */
  async updateWorldSettings(worldId, settings) {
    try {
      const response = await Gateway.http.world.put(`/api/worlds/${worldId}/settings`, settings);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update settings for world ${worldId}:`, error);
      throw error;
    }
  }

  /**
   * Сохранить мир
   * @param {string} worldId - ID мира
   * @returns {Promise<Object>} Результат сохранения
   */
  async saveWorld(worldId) {
    try {
      const response = await Gateway.http.world.post(`/api/worlds/${worldId}/save`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to save world ${worldId}:`, error);
      throw error;
    }
  }
}

export default new WorldGateway();